// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Import Material UI
import Divider from '@material-ui/core/Divider';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from "@material-ui/core/Grid";
import Checkbox from '@material-ui/core/Checkbox';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles({
    flexPaper: {
        flex: 1
    },
    root: {
        flexWrap: 'wrap',
        overflowY: 'auto',
        maxHeight: '530px'
    },
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
    emptyVariable: {
        display: 'flex',
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        minHeigth: '250px',
        color: '#f19595',
        fontWeight: 'bold'
    },
    marginTopZero: {
        marginTop: '0px'
    },
    divider: {
        marginLeft: '50%',
        marginTop: '40px',
        height: '98%'
    },
    title: {
        minWidth: '30%',
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 'bold'
    },
    tooltip: {
        top: "15px",
    },
})

const label = 'CONTENT.PUBLISH.';

const DraggableList = ({ variables, setVariables, changeOrders, changeVariable, type }) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const reorder = (list, startIndex, endIndex) => {

        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    }

    const onDragEnd = ({ destination, source }) => {
        if (!destination) return;
        const newItems = reorder(variables, source.index, destination.index);
        changeOrders(newItems)
        setVariables(newItems);
    }

    const onChangeVariable = (id, field) => {
        const copyVariables = Array.from(variables);
        const currentIndex = copyVariables.findIndex(x => x.id === id);

        copyVariables[currentIndex][field] = !copyVariables[currentIndex][field];
        setVariables(copyVariables);
        changeVariable(id, field);
    }

    return (
        <div className={classes.root}>
            <div className={classes.flexPaper}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId='droppable'>
                        {provided => (
                            <List ref={provided.innerRef} {...provided.droppableProps} subheader={
                                <ListSubheader style={{ padding: '0px' }} >
                                    <Tooltip classes={classes} arrow placement='top' title={"Must be marked for mandatory fields."}>
                                        <span className={classes.title}>{t(label + "REQUIRED")}</span>
                                    </Tooltip>
                                    {
                                        type === 'input' && <>
                                            <Tooltip classes={classes} arrow placement='top' title={"Must be checked to enable use of connection pool"}>
                                                <span className={classes.title}>Secret</span>
                                            </Tooltip>
                                            <span className={classes.title}>Variable</span>
                                        </>
                                    }
                                </ListSubheader>
                            }>
                                {variables.map((item, index) => {
                                    const labelId = `checkbox-list-label-${item.id}`
                                    return <Draggable key={item.id} draggableId={item.id} index={index}>
                                        {(provided, snapshot) => (
                                            <ListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={snapshot.isDragging ? classes.draggingListItem : ''}
                                                key={index} role={undefined} dense button >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        onClick={() => onChangeVariable(item.id, 'is_required')}
                                                        edge="start"
                                                        checked={item.is_required}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        inputProps={{ 'aria-labelledby': labelId }}
                                                    />
                                                </ListItemIcon>
                                                {
                                                    type === 'input' && (
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                disabled={item.type !== 'string'}
                                                                onClick={() => onChangeVariable(item.id, 'secret')}
                                                                edge="start"
                                                                checked={item.secret}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </ListItemIcon>
                                                    )
                                                }
                                                <ListItemText id={labelId} primary={item.name} secondary={item.type} />

                                            </ListItem>
                                        )}
                                    </Draggable>
                                })}
                                {provided.placeholder}
                            </List>

                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    )
}

const StepVariables = ({ variables, setVariables, setIsRequired }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [inputVariables, setInputVariables] = useState([]);
    const [outputVariables, setOutputVariables] = useState([]);

    useEffect(() => {
        let inputs = [];
        let outputs = [];
        let copyVariables = Array.from(variables);
        let inputOrder = Math.max.apply(Math, copyVariables.map(x => x.is_input ? x.order : 1));
        let outputOrder = Math.max.apply(Math, copyVariables.map(x => x.is_output ? x.order : 1));

        for (let i = 0; i < copyVariables.length; i++) {
            const item = copyVariables[i];
            if (item.is_input) {
                if (!item.hasOwnProperty('order')) {
                    copyVariables[i].is_required = false;
                    copyVariables[i].secret = false;
                    copyVariables[i].order = inputOrder;
                    inputOrder++;
                }
                inputs.push({ id: item.id, name: item.name, type: item.type, is_required: copyVariables[i].is_required, order: copyVariables[i].order, secret: copyVariables[i].secret });
                inputs.sort((a, b) => (a.order > b.order) ? 1 : -1)
            } else if (item.is_output) {
                if (!item.hasOwnProperty('order')) {
                    copyVariables[i].is_required = false;
                    copyVariables[i].order = outputOrder;
                    outputOrder++;
                }
                outputs.push({ id: item.id, name: item.name, type: item.type, is_required: copyVariables[i].is_required, order: copyVariables[i].order });
                outputs.sort((a, b) => (a.order > b.order) ? 1 : -1)
            }
        }

        setInputVariables(inputs);
        setOutputVariables(outputs);
        setIsRequired(false);
    }, [])

    const changeOrders = (newVariables) => {
        const copyVariables = Array.from(variables);
        newVariables.map((item, index) => {
            const i = copyVariables.findIndex(x => x.id === item.id);
            copyVariables[i].order = index + 1;
        })
        setVariables(copyVariables);
    }

    const changeVariable = (id, field) => {
        const copyVariables = Array.from(variables);
        const currentIndex = copyVariables.findIndex(x => x.id === id);
        copyVariables[currentIndex][field] = !copyVariables[currentIndex][field];

        setVariables(copyVariables);
    }

    return (
        <Grid container >
            <Grid item xs={5} style={{ marginTop: '20px' }}>
                <h2 className={classes.marginTopZero}>{t(label + "INPUT-VARIABLES")}</h2>
                {
                    inputVariables.length ? <DraggableList variables={inputVariables} setVariables={setInputVariables} changeOrders={changeOrders} changeVariable={changeVariable} type="input" /> :
                        <div className={classes.emptyVariable}>{t(label + "NO-VARIABLE-VIEW")} </div>
                }
            </Grid>
            <Grid item xs={2}>
                <Divider className={classes.divider} orientation={'vertical'} />
            </Grid>
            <Grid item xs={5} style={{ marginTop: '20px' }}>
                <h2 className={classes.marginTopZero}>{t(label + "OUTPUT-VARIABLES")}</h2>
                {
                    outputVariables.length ? <DraggableList variables={outputVariables} setVariables={setOutputVariables} changeOrders={changeOrders} changeVariable={changeVariable} type="output" /> :
                        <div className={classes.emptyVariable}>{t(label + "NO-VARIABLE-VIEW")} </div>
                }
            </Grid>
        </Grid >
    );
};


export default StepVariables;